import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useAddPayment } from "@/api/hooks/useInvoices";
import { dollarsToCents } from "@/lib/money";
import { PAYMENT_METHOD_LABELS, PaymentMethodSchema, type PaymentMethod } from "@/api/types/payment";

const PAYMENT_METHODS = PaymentMethodSchema.options;

interface RecordPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  onRecorded?: () => void;
}

/**
 * Logs a manual payment against an invoice (POST /v2/invoices/:id/payments).
 * Amount is authored in dollars and converted to integer cents only at
 * submit — never a stored computation (house rule). `received_at` is left
 * unset unless the caller picks a date, letting the server default to "now"
 * (contract marks it optional; see money.spec.ts / builder report for the
 * ambiguity this resolves).
 */
export function RecordPaymentDialog({ open, onClose, invoiceId, onRecorded }: RecordPaymentDialogProps) {
  const [method, setMethod] = useState<PaymentMethod>("zelle");
  const [amountDollars, setAmountDollars] = useState("");
  const [reference, setReference] = useState("");
  const [receivedAt, setReceivedAt] = useState("");

  const addPayment = useAddPayment();

  useEffect(() => {
    if (open) {
      setMethod("zelle");
      setAmountDollars("");
      setReference("");
      setReceivedAt("");
    }
  }, [open]);

  const amountCents = dollarsToCents(amountDollars);
  const canSubmit = amountCents > 0 && !addPayment.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await addPayment.mutateAsync({
        invoiceId,
        method,
        amount_cents: amountCents,
        reference: reference.trim() || undefined,
        received_at: receivedAt || undefined,
      });
      toastSuccess("Payment recorded");
      onRecorded?.();
      onClose();
    } catch (err) {
      toastError("Couldn't record payment", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Record payment">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Record payment</DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary" htmlFor="payment-method">
              Method
            </label>
            <select
              id="payment-method"
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              className="flex h-10 w-full rounded-md border border-border bg-bg-card px-3 text-sm"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </div>

          <FormField
            label="Amount"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            hint="In dollars — converted to cents on submit."
          />

          <FormField
            label="Reference (optional)"
            type="text"
            placeholder="e.g. Zelle confirmation #"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />

          <FormField
            label="Received on (optional)"
            type="date"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            hint="Leave blank to use today."
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={addPayment.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {addPayment.isPending ? "Saving..." : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
